import { useEffect } from 'react'

import { useConnectStore } from '~/app/connect/store'
import { useActiveOrg } from '~/hooks/org'
import { useActiveOrgSmartAccountsChainIds } from '~/hooks/org/useActiveOrgSmartAccountsChainIds'
import {
  SplitsConnectChainIdsByDomain,
  setExtensionChainIdsByDomain,
} from '~/utils/splitsConnectExtension'

type ActiveConnectionsByDomain = ReturnType<
  typeof useConnectStore.getState
>['activeConnections']

// Only the active org carries its enabled networks. The extension merges
// entries per site (0xSplits/splits-connect → src/utils/site-network.ts), so
// sites connected to other workspaces keep the list published when each was active.
const chainIdsByDomainForOrg = ({
  activeConnectionsByDomain,
  chainIds,
  orgId,
}: {
  activeConnectionsByDomain: ActiveConnectionsByDomain
  chainIds: number[]
  orgId: string
}): SplitsConnectChainIdsByDomain =>
  Object.fromEntries(
    Object.entries(activeConnectionsByDomain)
      .filter(([, connection]) => connection.orgId === orgId)
      .map(([domain]) => [domain, chainIds]),
  )

// Mirrors, to the splits-connect extension, which networks the active org has
// enabled for each site it is connected to. Re-posts whenever the org, its
// networks, or the connection store change.
export const useSyncExtensionConnectionNetworks = () => {
  const { org, isFetchedAfterMount } = useActiveOrg()
  const chainIds = useActiveOrgSmartAccountsChainIds()
  const activeConnectionsByDomain = useConnectStore(
    (state) => state.activeConnections,
  )

  // useActiveOrg seeds a placeholder org with no networks until the org fetch
  // resolves, and keeps it when the fetch fails. A completed org always has at
  // least one network, so an empty list is never a real answer to publish.
  const ready = org && isFetchedAfterMount && chainIds.length > 0
  const serialized = ready
    ? JSON.stringify(
        chainIdsByDomainForOrg({
          activeConnectionsByDomain,
          chainIds,
          orgId: org.id,
        }),
      )
    : null

  // eslint-disable-next-line local/noUseEffect -- mirroring state into an external system (the extension's storage) is synchronization an effect must do
  useEffect(() => {
    if (serialized === null) return
    const chainIdsByDomain = JSON.parse(
      serialized,
    ) as SplitsConnectChainIdsByDomain
    if (Object.keys(chainIdsByDomain).length === 0) return
    setExtensionChainIdsByDomain(chainIdsByDomain)
  }, [serialized])
}

export const __testing__ = { chainIdsByDomainForOrg }
